import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Users, BookOpen, Trophy, Flag, Shield, Star, UserCheck
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { AdminUsersTab } from '../components/admin/AdminUsersTab';
import { AdminFlagsTab } from '../components/admin/AdminFlagsTab';
import { AdminContentTab } from '../components/admin/AdminContentTab';
import { AdminModPermsTab } from '../components/admin/AdminModPermsTab';
import { AdminChallengesTab } from '../components/admin/AdminChallengesTab';
import { AdminSpotlightTab } from '../components/admin/AdminSpotlightTab';
import { AdminPendingApprovalsTab } from '../components/admin/AdminPendingApprovalsTab';

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, api, isAdmin, isModerator } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [flags, setFlags] = useState([]);
  const [genres, setGenres] = useState([]);
  const [allStories, setAllStories] = useState([]);
  const [allChallenges, setAllChallenges] = useState([]);
  const [loading, setLoading] = useState(true);

  // Mod Permissions
  const [selectedMod, setSelectedMod] = useState(null);
  const [modPermissions, setModPermissions] = useState({});
  const [savingPerms, setSavingPerms] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [genresRes] = await Promise.all([
        api.get('/genres')
      ]);
      setGenres(genresRes.data);

      if (isAdmin) {
        const [statsRes, usersRes, flagsRes, storiesRes, challengesRes] = await Promise.all([
          api.get('/admin/stats'),
          api.get('/users?limit=50'),
          api.get('/flags?resolved=false'),
          api.get('/stories?limit=200'),
          api.get('/challenges?limit=100')
        ]);
        setStats(statsRes.data);
        setUsers(usersRes.data);
        setFlags(flagsRes.data);
        setAllStories(storiesRes.data);
        setAllChallenges(challengesRes.data);
      }
    } catch (error) {
      console.error('Failed to load admin data:', error);
    } finally {
      setLoading(false);
    }
  }, [api, isAdmin]);

  useEffect(() => {
    if (!isModerator) {
      navigate('/');
      return;
    }

    fetchData();
  }, [fetchData, isModerator, navigate]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.post(`/admin/set-role?user_id=${userId}&role=${newRole}`);
      setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
    } catch (error) {
      console.error('Failed to change role:', error);
    }
  };

  const handleResolveFlag = async (flagId) => {
    try {
      await api.put(`/flags/${flagId}/resolve`);
      setFlags(flags.filter(f => f.id !== flagId));
      if (stats) {
        setStats({ ...stats, pending_flags: stats.pending_flags - 1 });
      }
    } catch (error) {
      console.error('Failed to resolve flag:', error);
    }
  };

  const handleDeleteStory = async (storyId, storyTitle) => {
    if (!window.confirm(`Delete "${storyTitle}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/stories/${storyId}`);
      setAllStories(allStories.filter(s => s.id !== storyId));
    } catch (error) {
      console.error('Failed to delete story:', error);
      alert('Failed to delete story');
    }
  };

  const handleDeleteChallenge = async (challengeId, title) => {
    if (!window.confirm(`Delete challenge "${title}" and all its entries? This cannot be undone.`)) return;
    try {
      await api.delete(`/challenges/${challengeId}`);
      setAllChallenges(allChallenges.filter(c => c.id !== challengeId));
    } catch (error) {
      console.error('Failed to delete challenge:', error);
      alert('Failed to delete challenge');
    }
  };

  const handleSelectMod = async (mod) => {
    setSelectedMod(mod);
    try {
      const res = await api.get(`/admin/mod-permissions/${mod.id}`);
      setModPermissions(res.data);
    } catch (error) {
      console.error('Failed to load mod permissions:', error);
      setModPermissions({
        can_create_challenges: false,
        can_judge_challenges: false,
        can_manage_flags: false,
        can_warn_users: false,
        can_suspend_users: false,
      });
    }
  };

  const handleSaveModPermissions = async () => {
    if (!selectedMod) return;
    setSavingPerms(true);
    try {
      await api.put(`/admin/mod-permissions/${selectedMod.id}`, modPermissions);
      alert(`Permissions updated for ${selectedMod.display_name || selectedMod.username}`);
    } catch (error) {
      console.error('Failed to save permissions:', error);
      alert('Failed to save permissions');
    } finally {
      setSavingPerms(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <Skeleton className="h-12 w-64 mb-8" />
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="admin-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Shield className="w-8 h-8 text-brand-accent" />
          <div>
            <h1 className="font-serif text-3xl font-semibold text-text-primary">Admin Panel</h1>
            <p className="text-text-secondary">
              {isAdmin ? 'Full admin access' : 'Moderator access'}
            </p>
          </div>
        </div>
      </div>

      {/* Stats (Admin only) */}
      {isAdmin && stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
          <Card className="bg-white border-brand-primary/10">
            <CardContent className="p-6 text-center">
              <Users className="w-8 h-8 text-brand-primary mx-auto mb-2" />
              <p className="text-2xl font-semibold text-text-primary">{stats.total_users}</p>
              <p className="text-sm text-text-muted">Users</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-brand-primary/10">
            <CardContent className="p-6 text-center">
              <BookOpen className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-semibold text-text-primary">{stats.total_stories}</p>
              <p className="text-sm text-text-muted">Stories</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-brand-primary/10">
            <CardContent className="p-6 text-center">
              <Trophy className="w-8 h-8 text-brand-tertiary mx-auto mb-2" />
              <p className="text-2xl font-semibold text-text-primary">{stats.total_challenges}</p>
              <p className="text-sm text-text-muted">Challenges</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-brand-primary/10">
            <CardContent className="p-6 text-center">
              <Users className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <p className="text-2xl font-semibold text-text-primary">{stats.total_groups}</p>
              <p className="text-sm text-text-muted">Groups</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-brand-primary/10">
            <CardContent className="p-6 text-center">
              <Flag className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <p className="text-2xl font-semibold text-text-primary">{stats.pending_flags}</p>
              <p className="text-sm text-text-muted">Pending Flags</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="challenges">
        <TabsList className="bg-surface-alt mb-8 overflow-x-auto">
          <TabsTrigger value="challenges" data-testid="tab-challenges">
            <Trophy className="w-4 h-4 mr-2" />
            Challenges
          </TabsTrigger>
          {isAdmin && (
            <>
              <TabsTrigger value="users" data-testid="tab-users">
                <Users className="w-4 h-4 mr-2" />
                Users
              </TabsTrigger>
              <TabsTrigger value="flags" data-testid="tab-flags">
                <Flag className="w-4 h-4 mr-2" />
                Flags {flags.length > 0 && <Badge className="ml-2 bg-red-500">{flags.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="content" data-testid="tab-content">
                <BookOpen className="w-4 h-4 mr-2" />
                Content
              </TabsTrigger>
              <TabsTrigger value="mod-perms" data-testid="tab-mod-perms">
                <Shield className="w-4 h-4 mr-2" />
                Mod Permissions
              </TabsTrigger>
              <TabsTrigger value="spotlight" data-testid="tab-spotlight">
                <Star className="w-4 h-4 mr-2" />
                Spotlight
              </TabsTrigger>
              <TabsTrigger value="pending" data-testid="tab-pending-approvals">
                <UserCheck className="w-4 h-4 mr-2" />
                Pending Approvals {stats?.pending_approvals > 0 && <Badge className="ml-2 bg-brand-accent">{stats.pending_approvals}</Badge>}
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* Challenges Tab */}
        <TabsContent value="challenges">
          <AdminChallengesTab
            allChallenges={allChallenges}
            genres={genres}
            api={api}
            onDeleteChallenge={handleDeleteChallenge}
          />
        </TabsContent>

        {/* Users Tab (Admin only) */}
        {isAdmin && (
          <TabsContent value="users">
            <AdminUsersTab users={users} user={user} api={api} fetchData={fetchData} handleRoleChange={handleRoleChange} />
          </TabsContent>
        )}

        {/* Flags Tab (Admin only) */}
        {isAdmin && (
          <TabsContent value="flags">
            <AdminFlagsTab flags={flags} handleResolveFlag={handleResolveFlag} />
          </TabsContent>
        )}

        {/* Content Management Tab (Admin only) */}
        {isAdmin && (
          <TabsContent value="content">
            <AdminContentTab allStories={allStories} handleDeleteStory={handleDeleteStory} />
          </TabsContent>
        )}

        {/* Mod Permissions Tab (Admin only) */}
        {isAdmin && (
          <TabsContent value="mod-perms">
            <AdminModPermsTab
              users={users}
              selectedMod={selectedMod}
              modPermissions={modPermissions}
              savingPerms={savingPerms}
              handleSelectMod={handleSelectMod}
              setModPermissions={setModPermissions}
              handleSaveModPermissions={handleSaveModPermissions}
            />
          </TabsContent>
        )}

        {/* Spotlight Tab (Admin only) */}
        {isAdmin && (
          <TabsContent value="spotlight">
            <AdminSpotlightTab api={api} />
          </TabsContent>
        )}

        {/* Pending Approvals Tab (Admin only) */}
        {isAdmin && (
          <TabsContent value="pending">
            <AdminPendingApprovalsTab api={api} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default AdminPage;
